import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import GlobalWorkout from "../models/GlobalWorkout";

const CATEGORIES = [
  { key: "EMOM", label: "EMOM", tagline: "Every Minute On the Minute", gradient: "linear-gradient(140deg, #6c63ff 0%, #5a52e8 100%)" },
  { key: "AMRAP", label: "AMRAP", tagline: "As Many Rounds As Possible", gradient: "linear-gradient(140deg, #4f8cff 0%, #3a70e6 100%)" },
  { key: "ForTime", label: "FOR TIME", tagline: "Complete work ASAP", gradient: "linear-gradient(140deg, #2ecc8b 0%, #1fa872 100%)" },
  { key: "HIIT", label: "HIIT", tagline: "High-intensity intervals", gradient: "linear-gradient(140deg, #ef5b5b 0%, #d63f3f 100%)" },
  { key: "Strength", label: "STRENGTH", tagline: "Resistance training", gradient: "linear-gradient(140deg, #b56bff 0%, #9c4be5 100%)" },
  { key: "Endurance", label: "ENDURANCE", tagline: "Cardio & stamina", gradient: "linear-gradient(140deg, #2dd4d4 0%, #1bb1b1 100%)" },
];

const NORMALIZED_CATEGORY = {
  "EMOM": "EMOM",
  "AMRAP": "AMRAP",
  "FOR TIME": "ForTime",
  "FORTIME": "ForTime",
  "HIIT": "HIIT",
  "STRENGTH": "Strength",
  "ENDURANCE": "Endurance",
  "CHIPPER": "ForTime",
  "OTHER": "Other",
};

const normalizeCategory = (raw) => {
  if (!raw) return "Other";
  const key = String(raw).trim().toUpperCase();
  return NORMALIZED_CATEGORY[key] || "Other";
};

const Banner = () => (
  <div style={{
    background: "rgba(99,102,241,0.12)",
    border: "1px solid rgba(99,102,241,0.35)",
    color: "#d8d9ff",
    borderRadius: 14,
    padding: "14px 18px",
    fontSize: 14,
    lineHeight: 1.5,
    marginBottom: 32,
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  }}>
    <span style={{ fontSize: 18 }}>👀</span>
    <span style={{ flex: 1, minWidth: 220 }}>
      You're browsing in preview mode. Download the app to log workouts, save favorites, and use the built-in timer.
    </span>
    <Link to="/" style={{ color: "#a5a8ff", textDecoration: "underline", fontWeight: 600, fontSize: 13 }}>
      Back to landing
    </Link>
  </div>
);

const CategoryCard = ({ category, count, selected, onClick }) => (
  <button
    onClick={onClick}
    style={{
      border: selected ? "2px solid #ffffff" : "2px solid transparent",
      borderRadius: 18,
      padding: 0,
      background: "transparent",
      cursor: "pointer",
      textAlign: "left",
      transition: "transform 0.15s",
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; }}
    onMouseLeave={e => { e.currentTarget.style.transform = ""; }}
  >
    <div style={{
      background: category.gradient,
      borderRadius: 16,
      padding: 20,
      minHeight: 130,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      color: "white",
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.85 }}>
        {count} {count === 1 ? "workout" : "workouts"}
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1, marginBottom: 4 }}>
          {category.label}
        </div>
        <div style={{ fontSize: 13, opacity: 0.9 }}>
          {category.tagline}
        </div>
      </div>
    </div>
  </button>
);

const WorkoutCard = ({ workout, expanded, onToggle }) => (
  <div style={{
    background: "#0f0f0f",
    border: "1px solid #1f1f1f",
    borderRadius: 16,
    overflow: "hidden",
    transition: "border-color 0.2s",
  }}>
    <button
      onClick={onToggle}
      style={{
        width: "100%",
        background: "transparent",
        border: "none",
        color: "inherit",
        padding: "18px 20px",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "#a5a8ff", marginBottom: 6 }}>
          {workout.category || "WORKOUT"}
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: "#ffffff", marginBottom: 4 }}>
          {workout.name}
        </div>
        {workout.description && (
          <div style={{ fontSize: 14, color: "#888888", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: expanded ? 99 : 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {workout.description}
          </div>
        )}
      </div>
      <div style={{
        fontSize: 22,
        color: "#666666",
        transform: expanded ? "rotate(180deg)" : "",
        transition: "transform 0.2s",
        flexShrink: 0,
      }}>
        ⌄
      </div>
    </button>

    {expanded && Array.isArray(workout.exercises) && workout.exercises.length > 0 && (
      <div style={{ padding: "0 20px 20px", borderTop: "1px solid #1a1a1a" }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.5, color: "#666666", textTransform: "uppercase", margin: "16px 0 12px" }}>
          Exercises
        </div>
        <ol style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {workout.exercises.map((ex, idx) => (
            <li key={idx} style={{
              display: "flex",
              gap: 14,
              padding: "10px 0",
              borderBottom: idx < workout.exercises.length - 1 ? "1px solid #1a1a1a" : "none",
            }}>
              <div style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                background: "rgba(99,102,241,0.15)",
                color: "#a5a8ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
              }}>
                {idx + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#ffffff", fontSize: 15, fontWeight: 600 }}>{ex.name}</div>
                {(ex.targetReps !== undefined && ex.targetReps !== null && ex.targetReps !== "") && (
                  <div style={{ color: "#888888", fontSize: 13, marginTop: 2 }}>
                    {String(ex.targetReps)} {typeof ex.targetReps === "number" ? "reps" : ""}
                  </div>
                )}
                {ex.notes && (
                  <div style={{ color: "#666666", fontSize: 13, marginTop: 4, fontStyle: "italic" }}>
                    {ex.notes}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    )}
  </div>
);

const PreviewPage = () => {
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await GlobalWorkout.getAll({ requirePopularity: false });
        if (!cancelled) {
          setWorkouts(list || []);
          setLoading(false);
        }
      } catch (err) {
        console.error("Preview load failed:", err);
        if (!cancelled) {
          setError("Couldn't load the workout library. Try refreshing.");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const workoutsByCategory = useMemo(() => {
    const map = {};
    workouts.forEach((w) => {
      const cat = normalizeCategory(w.category);
      if (!map[cat]) map[cat] = [];
      map[cat].push(w);
    });
    return map;
  }, [workouts]);

  const visibleWorkouts = useMemo(() => {
    if (!selectedCategory) return workouts;
    return workoutsByCategory[selectedCategory] || [];
  }, [selectedCategory, workouts, workoutsByCategory]);

  return (
    <div style={{
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: "#000000",
      color: "#ffffff",
      minHeight: "100vh",
      padding: "48px 20px 80px",
    }}>
      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        <div style={{ marginBottom: 32 }}>
          <Link to="/" style={{ color: "#888888", textDecoration: "none", fontSize: 14 }}>
            ← Back to home
          </Link>
        </div>

        <header style={{ marginBottom: 32 }}>
          <p style={{ fontSize: 13, fontWeight: 600, letterSpacing: 3, color: "#6366f1", textTransform: "uppercase", margin: "0 0 12px" }}>
            Workout library
          </p>
          <h1 style={{ fontSize: "clamp(28px, 5vw, 40px)", fontWeight: 800, margin: "0 0 12px" }}>
            Browse what's inside.
          </h1>
          <p style={{ color: "#aaaaaa", fontSize: 17, margin: 0, maxWidth: 580, lineHeight: 1.5 }}>
            Every workout in the app's shared library. No sign-in required to look around.
          </p>
        </header>

        <Banner />

        <section style={{ marginBottom: 40 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Categories</h2>
            {selectedCategory && (
              <button
                onClick={() => setSelectedCategory(null)}
                style={{
                  background: "transparent",
                  border: "1px solid #333333",
                  color: "#aaaaaa",
                  borderRadius: 999,
                  padding: "6px 14px",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Show all
              </button>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
            {CATEGORIES.map((cat) => (
              <CategoryCard
                key={cat.key}
                category={cat}
                count={(workoutsByCategory[cat.key] || []).length}
                selected={selectedCategory === cat.key}
                onClick={() => setSelectedCategory(selectedCategory === cat.key ? null : cat.key)}
              />
            ))}
          </div>
        </section>

        <section>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>
            {selectedCategory ? `${CATEGORIES.find(c => c.key === selectedCategory)?.label} workouts` : "All workouts"}
            <span style={{ color: "#666666", fontWeight: 500, marginLeft: 10 }}>
              ({visibleWorkouts.length})
            </span>
          </h2>

          {loading && (
            <div style={{ color: "#888888", padding: 40, textAlign: "center" }}>
              Loading workouts…
            </div>
          )}

          {error && (
            <div style={{ color: "#ef5b5b", background: "#1a0a0a", border: "1px solid #3a1a1a", borderRadius: 12, padding: 20 }}>
              {error}
            </div>
          )}

          {!loading && !error && visibleWorkouts.length === 0 && (
            <div style={{ color: "#888888", padding: 40, textAlign: "center", background: "#0a0a0a", border: "1px dashed #222222", borderRadius: 14 }}>
              No workouts in this category yet.
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {visibleWorkouts.map((w) => (
              <WorkoutCard
                key={w.id}
                workout={w}
                expanded={expandedId === w.id}
                onToggle={() => setExpandedId(expandedId === w.id ? null : w.id)}
              />
            ))}
          </div>
        </section>

        <footer style={{ marginTop: 60, textAlign: "center", color: "#666666", fontSize: 14 }}>
          Like what you see? <Link to="/" style={{ color: "#a5a8ff" }}>Grab the app</Link> to log workouts and track progress.
        </footer>
      </div>
    </div>
  );
};

export default PreviewPage;
